<template>
    <StatisticsLayout :title="$t('statistics.mode_segments')" :is-empty="(stats.games ?? 0) === 0">
        <template #summary>
            <div class="flex flex-col gap-6">
                <TabBar
                    :model-value="selectedMode"
                    :tabs="tabs"
                    class="-mx-4 -mt-2"
                    @update:model-value="selectMode"
                />
                <div class="grid grid-cols-2 gap-x-4">
                    <StatsHeroTile
                        :title="$t('statistics.total_games')"
                        :value="stats.games ?? 0"
                        :footnote="
                            $t('statistics.vs_last_month', {
                                delta: signed(stats.games_vs_last_month ?? 0),
                            })
                        "
                    />
                    <StatsHeroTile
                        variant="light"
                        :title="$t('statistics.daily_streak')"
                        :value="stats.daily_streak?.current ?? 0"
                        :footnote="
                            $t('statistics.highest', { count: stats.daily_streak?.highest ?? 0 })
                        "
                    />
                </div>
            </div>
        </template>

        <WinrateCard v-bind="stats.winrate" />

        <SegmentAccuracyCard
            v-if="topTargets.length"
            :badge="$t('statistics.top_5')"
            :results="topTargets"
            :show-rings="selectedMode === 'points'"
            positive
        />
        <SegmentAccuracyCard
            v-if="bottomTargets.length"
            :badge="$t('statistics.bottom_5')"
            :results="bottomTargets"
            :show-rings="selectedMode === 'points'"
        />

        <Panel size="full" padding="p-4" :corners="false">
            <h2 class="text-base leading-6 font-black tracking-[0.25px] uppercase">
                {{ $t('statistics.other_statistics') }}
            </h2>
            <StatsList :rows="otherStatistics" class="mt-4" />
        </Panel>
        <PersonalRecords :records="records" />
    </StatisticsLayout>
</template>

<script>
import { mapStores } from 'pinia'
import { useStatisticsStore } from '@/stores/statistics.js'
import StatisticsLayout from '@/components/statistics/StatisticsLayout.vue'
import StatsHeroTile from '@/components/statistics/StatsHeroTile.vue'
import StatsList from '@/components/statistics/StatsList.vue'
import WinrateCard from '@/components/statistics/WinrateCard.vue'
import PersonalRecords from '@/components/statistics/PersonalRecords.vue'
import SegmentAccuracyCard from '@/components/statistics/SegmentAccuracyCard.vue'
import TabBar from '@/components/ui/TabBar.vue'
import Panel from '@/components/layout/Panel.vue'

export default {
    name: 'SegmentsStatsView',
    components: {
        StatisticsLayout,
        StatsHeroTile,
        StatsList,
        WinrateCard,
        PersonalRecords,
        SegmentAccuracyCard,
        TabBar,
        Panel,
    },
    computed: {
        records() {
            const rings = {
                singles: this.$t('match_settings.ring_singles'),
                doubles: this.$t('match_settings.ring_doubles'),
                triples: this.$t('match_settings.ring_triples'),
                mixed: this.$t('statistics.mixed'),
            }
            const orders = [
                this.$t('match_settings.target_order_low_to_high'),
                this.$t('match_settings.target_order_high_to_low'),
                this.$t('match_settings.target_order_random'),
            ]
            return (this.stats.records ?? []).map((record) => ({
                title:
                    this.selectedMode === 'points'
                        ? this.$t('statistics.record_points', { count: record.value })
                        : this.$t('statistics.record_darts', { count: record.value }),
                details: [
                    this.selectedMode === 'points'
                        ? record.points_mode === 1
                            ? this.$t('statistics.rounds_value', { count: record.rounds })
                            : this.$t('statistics.ceiling_value', { count: record.points_ceiling })
                        : (rings[record.rings] ?? rings.mixed),
                    orders[record.target_order] ?? orders[0],
                ],
                playedAt: record.played_at,
            }))
        },

        ...mapStores(useStatisticsStore),

        selectedMode() {
            return this.$route.query.mode === 'points' ? 'points' : 'race'
        },

        tabs() {
            return [
                {
                    id: 'race',
                    label: this.$t('match_settings.segment_mode_race'),
                    testId: 'segments-tab-race',
                },
                {
                    id: 'points',
                    label: this.$t('match_settings.segment_mode_points'),
                    testId: 'segments-tab-points',
                },
            ]
        },

        stats() {
            return (
                this.statisticsStore.allTime?.mode_stats?.matchtype_segments?.modes?.[
                    this.selectedMode
                ] ?? {}
            )
        },

        topTargets() {
            return (this.stats.target_accuracy ?? []).slice(0, 5)
        },

        bottomTargets() {
            const targets = this.stats.target_accuracy ?? []
            return targets.slice(Math.max(5, targets.length - 5))
        },

        otherStatistics() {
            const round = (value) => (value == null ? '-' : String(Math.round(value * 10) / 10))
            const percentage = (value) => (value == null ? '-' : `${Math.round(value * 10) / 10}%`)

            if (this.selectedMode === 'points') {
                return [
                    {
                        label: this.$t('statistics.average_score'),
                        value: round(this.stats.average_score),
                    },
                    {
                        label: this.$t('statistics.highest_score'),
                        value: round(this.stats.highest_score),
                    },
                    {
                        label: this.$t('statistics.average_accuracy'),
                        value: percentage(this.stats.accuracy),
                    },
                ]
            }

            return [
                {
                    label: this.$t('statistics.average_darts_per_game'),
                    value: round(this.stats.average_darts),
                },
                {
                    label: this.$t('statistics.lowest_darts_per_game'),
                    value: round(this.stats.lowest_darts),
                },
                {
                    label: this.$t('statistics.average_accuracy'),
                    value: percentage(this.stats.accuracy),
                },
            ]
        },
    },
    methods: {
        selectMode(mode) {
            this.$router.replace({ query: mode === 'points' ? { mode } : {} })
        },

        signed(delta) {
            return delta > 0 ? `+${delta}` : String(delta)
        },
    },
}
</script>
